import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight, XCircle } from "lucide-react";
import api from "@/lib/api";

const MAX = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [program, setProgram] = useState("");

  useEffect(() => {
    if (params.get("provider") === "paypal") { setState("paid"); return; }
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setProgram(data.program_name || "");
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.payment_status === "expired" || data.status === "failed") { setState("error"); return; }
      } catch { /* keep trying */ }
      attempts += 1;
      if (attempts >= MAX) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-soma-bg flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
        className="text-center max-w-lg" data-testid="payment-success-page">
        {state === "checking" && (<>
          <Loader2 className="w-14 h-14 text-soma-primary mx-auto mb-8 animate-spin" strokeWidth={1.5} />
          <h1 className="font-display font-bold text-3xl mb-3">Confirming your payment…</h1>
          <p className="text-soma-muted">Hold tight, this only takes a moment.</p>
        </>)}
        {state === "paid" && (<>
          <div className="w-20 h-20 rounded-full bg-soma-primary/15 flex items-center justify-center mx-auto mb-8 cyan-glow">
            <CheckCircle2 className="w-10 h-10 text-soma-primary" strokeWidth={1.5} />
          </div>
          <h1 className="font-display font-bold text-4xl mb-3">You're in.</h1>
          <p className="text-soma-muted mb-10">{program ? `${program} is now active on your account.` : "Your subscription is now active."}</p>
          <Link to="/dashboard" data-testid="success-to-dashboard"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-soma-primary text-soma-bg font-semibold hover:bg-soma-primaryHover transition-colors">
            Go to dashboard <ArrowRight className="w-4 h-4" />
          </Link>
        </>)}
        {(state === "timeout" || state === "error") && (<>
          <XCircle className="w-14 h-14 text-yellow-400 mx-auto mb-8" strokeWidth={1.5} />
          <h1 className="font-display font-bold text-3xl mb-3">{state === "timeout" ? "Still processing" : "Something went wrong"}</h1>
          <p className="text-soma-muted mb-10">Your payment may still be confirming. Check your dashboard shortly.</p>
          <Link to="/dashboard" className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/15 hover:border-soma-primary transition-colors">
            Go to dashboard
          </Link>
        </>)}
      </motion.div>
    </div>
  );
}
